import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { ActivityIndicator, Modal, Platform, ScrollView, TextInput, TouchableOpacity, View } from 'react-native'
import { AppBackground, ResponsiveLogo } from '@ui/components'

import { travelApi, type IAirlineRequest, type AirlineReviewDecision } from '@front/logic'

import { BodyText, Headline1, colors } from '@/aena/styles'
import { openStoredDocument, downloadJson } from '@/aena/travel/docUtils'

// Co-branding: logo Aena corporativo (aena_logo.svg) + wordmark petpass
// (digital_petpass_svg.svg), igual que en la pantalla de boarding.
import { ReactComponent as AenaPetsLogo } from '@/assets/icons/aena_logo.svg'
import { ReactComponent as PetPassLogo } from '@/assets/icons/digital_petpass_svg.svg'
import { ReactComponent as AenaDog } from '@/assets/icons/aena/aena_dog.svg'
import { ReactComponent as AenaCat } from '@/assets/icons/aena/aena_cat.svg'
import { ReactComponent as AirportIcon } from '@/assets/icons/airport_icon.svg'
import { ReactComponent as ChevronRight } from '@/assets/icons/arrow_chevron_right.svg'
import { ReactComponent as AlertTriangle } from '@/assets/icons/aena/aena_alert_triangle.svg'
import { ReactComponent as RoundedCheck } from '@/assets/icons/rounded_check.svg'
import { ReactComponent as PendingClock } from '@/assets/icons/pending-clock-orange-outline.svg'
import { ReactComponent as DocIcon } from '@/assets/icons/aena/aena_documentacion.svg'
import { ReactComponent as VacIcon } from '@/assets/icons/aena/aena_vacunacion.svg'
import { ReactComponent as ShieldPaw } from '@/assets/icons/shield_paw.svg'
import { ReactComponent as PawColor } from '@/assets/icons/paw_color.svg'
import { ReactComponent as SearchIcon } from '@/assets/icons/search_icon.svg'

// ===================== TOKENS =====================
const navy = colors.azulCorporativo // '#1a2732'
const accent = colors.aenaVerde // '#96ce00'
const accentInk = '#1a2732'
// Superficies
const canvas = '#FBFBFD'
const surface = '#FFFFFF'
const subtle = '#F6F7F9'
const hairline = '#ECEEF1'
// Tinta
const ink = '#1A2732'
const ink2 = '#5B6573'
const ink3 = '#9AA2AD'
// Especie (solo fondo del avatar)
const dogTint = '#EEF7E6'
const catTint = '#E9F0F7'
// Estados pastel {bg, fg}
const okBg = '#EEF7E6'
const okFg = '#5B8A1F'
const infoBg = '#EAF1F8'
const infoFg = '#2F6BB0'
const warnBg = '#FBF3E4'
const warnFg = '#B5781A'
const blockBg = '#FBECEC'
const blockFg = '#B23B3B'
const neutralBg = '#F1F2F4'
const neutralFg = '#5B6573'

// ===================== SOMBRAS (props RN nativas) =====================
const shadowCard = { shadowColor: '#1a2732', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.06, shadowRadius: 14, elevation: 2 } as const
const shadowSoft = { shadowColor: '#1a2732', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 8, elevation: 1 } as const
const shadowSticky = { shadowColor: '#1a2732', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 8, elevation: 3 } as const
const shadowFooter = { shadowColor: '#1a2732', shadowOffset: { width: 0, height: -2 }, shadowOpacity: 0.06, shadowRadius: 10, elevation: 8 } as const
const shadowAccept = { shadowColor: '#96ce00', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 10, elevation: 3 } as const

type SvgIcon = React.FC<React.SVGProps<SVGSVGElement> & { width?: number; height?: number }>

// ===================== HELPERS =====================

// dd/MM/yyyy como en el resto de pantallas de viaje.
const formatFlightDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return dateStr
    const dd = String(date.getDate()).padStart(2, '0')
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const yyyy = date.getFullYear()
    return `${dd}/${mm}/${yyyy}`
  } catch {
    return dateStr
  }
}

// yyyy-MM-dd (formato del <input type="date"> nativo y clave estable para
// comparar la fecha del vuelo con la fecha elegida en el filtro, sin la deriva
// de zona horaria que provoca toISOString()).
const toISODateKey = (dateStr: string): string => {
  try {
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return ''
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  } catch {
    return ''
  }
}

// FIX BUG doble código: flightNumber ya incluye el código de aerolínea ('UX0987').
const flightLabel = (req: IAirlineRequest): string => {
  const fn = (req.flightNumber || '').trim()
  if (fn) return fn
  return `${req.airlineCode || ''}`.trim() || '—'
}

interface StatusMeta {
  label: string
  bg: string
  fg: string
  useClock: boolean
}

const statusMeta = (req: IAirlineRequest): StatusMeta => {
  const s = (req.submissionStatus || '').toUpperCase()
  switch (s) {
    case 'READY':
      return { label: 'Lista para revisar', bg: infoBg, fg: infoFg, useClock: false }
    case 'RECEIVED':
      return { label: 'Recibida', bg: infoBg, fg: infoFg, useClock: false }
    case 'PENDING_REVIEW':
      return { label: 'En revisión', bg: warnBg, fg: warnFg, useClock: true }
    case 'REQUIRES_DOCUMENTS':
      return { label: 'Faltan documentos', bg: warnBg, fg: warnFg, useClock: true }
    case 'ACCEPTED':
      return { label: 'Aprobada', bg: okBg, fg: okFg, useClock: false }
    case 'REJECTED':
      return { label: 'Rechazada', bg: blockBg, fg: blockFg, useClock: false }
    default:
      break
  }
  switch (req.uiStatus) {
    case 'confirmed':
    case 'completed':
      return { label: 'Aprobada', bg: okBg, fg: okFg, useClock: false }
    case 'cancelled':
      return { label: 'Rechazada', bg: blockBg, fg: blockFg, useClock: false }
    case 'pending':
      return { label: 'Pendiente', bg: warnBg, fg: warnFg, useClock: true }
    default:
      return { label: 'Pendiente', bg: neutralBg, fg: neutralFg, useClock: false }
  }
}

const VALIDATION_LABELS: Record<string, string> = {
  passport: 'Pasaporte (exterior)',
  rabies_vaccine: 'Vacuna antirrábica',
  microchip_iso11784: 'Microchip',
  pet_weight: 'Peso',
  liability_release: 'Descargo (F244)',
  pnr_passenger: 'Localizador',
  destination_rules: 'Normas de destino',
}
const validationLabel = (type: string): string => VALIDATION_LABELS[type] || type

interface ValidationMeta {
  bg: string
  fg: string
  Icon: SvgIcon
  label: string
}

const validationMeta = (status: string): ValidationMeta => {
  const s = (status || '').toUpperCase()
  if (s === 'VALID') return { bg: okBg, fg: okFg, Icon: RoundedCheck as SvgIcon, label: 'Válido' }
  if (s === 'MISSING') return { bg: blockBg, fg: blockFg, Icon: AlertTriangle as SvgIcon, label: 'Falta' }
  if (s === 'INVALID' || s === 'REJECTED') return { bg: blockBg, fg: blockFg, Icon: AlertTriangle as SvgIcon, label: 'No válido' }
  return { bg: warnBg, fg: warnFg, Icon: PendingClock as SvgIcon, label: 'Sin verificar' }
}

interface DocConfig {
  Icon: SvgIcon
  label: string
}

const docConfig = (type: string): DocConfig => {
  switch (type) {
    case 'passport':
      return { Icon: DocIcon as SvgIcon, label: 'Pasaporte digital' }
    case 'rabies_certificate':
      return { Icon: VacIcon as SvgIcon, label: 'Vacunación' }
    case 'liability_release':
      return { Icon: DocIcon as SvgIcon, label: 'Descargo (F244)' }
    case 'full_report':
      return { Icon: ShieldPaw as SvgIcon, label: 'Certificado PetPass' }
    default:
      return { Icon: DocIcon as SvgIcon, label: type }
  }
}

const speciesAvatar = (species?: string): { Icon: SvgIcon; tint: string } => {
  const isCat = (species || '').toUpperCase() === 'CAT'
  return { Icon: (isCat ? AenaCat : AenaDog) as SvgIcon, tint: isCat ? catTint : dogTint }
}

const routeText = (req: IAirlineRequest): string => {
  const r = (req.route || '').trim()
  if (r) return r
  return `${req.departureAirport || '—'} → ${req.arrivalAirport || '—'}`
}

const transportLabel = (pet?: IAirlineRequest['pet']): string => {
  if (pet?.travelType) return pet.travelType
  return pet?.transportMode === 'HOLD' ? 'Bodega' : 'Cabina'
}

// Animal de asistencia (invidente / sordo / otra). Solo en estos casos se exigen y
// se muestran como aportados los certificados de asistencia (en el modal y el cert).
const isAssistancePet = (pet?: IAirlineRequest['pet']): boolean =>
  (pet?.roleGuid || '').toUpperCase().startsWith('ASSISTANCE')

const ASSISTANCE_REQUIRED_DOCS = [
  'Certificado de salud del pasajero',
  'Certificado de capacitación de la mascota',
]

// Validaciones VISIBLES: "Normas de destino" (destination_rules) y "Pasaporte (exterior)"
// (passport = pasaporte UE / documentación para viajes al extranjero) SOLO aplican a viajes
// INTERNACIONALES. En domésticos se ocultan esas filas y, por tanto, no cuentan en el medidor
// x/total: el pasaporte es OPCIONAL para viajar dentro de España. Helper único usado en TODOS
// los sitios donde se renderizan o cuentan validaciones (card, fila de tabla, checklist del modal).
const INTERNATIONAL_ONLY_VALIDATIONS = ['destination_rules', 'passport']
const visibleValidations = (req: IAirlineRequest): IAirlineRequest['validations'] =>
  req.isInternational
    ? req.validations
    : req.validations.filter((v) => !INTERNATIONAL_ONLY_VALIDATIONS.includes(v.type))

const validationCount = (req: IAirlineRequest): { valid: number; total: number; allOk: boolean } => {
  const list = visibleValidations(req)
  const total = list.length
  const valid = list.filter((v) => (v.status || '').toUpperCase() === 'VALID').length
  return { valid, total, allOk: total > 0 && valid === total }
}

// Documentos OCULTOS: 'passport' y 'rabies_certificate' (su info ya va dentro
// del Certificado PetPass). Se mantiene el resto.
const HIDDEN_DOC_TYPES = ['passport', 'rabies_certificate']
const visibleDocuments = (req: IAirlineRequest): IAirlineRequest['documents'] =>
  req.documents.filter((doc) => !HIDDEN_DOC_TYPES.includes(doc.type))

const isPending = (r: IAirlineRequest): boolean =>
  r.uiStatus === 'pending' ||
  ['READY', 'RECEIVED', 'PENDING_REVIEW', 'REQUIRES_DOCUMENTS'].includes((r.submissionStatus || '').toUpperCase())

const isApproved = (r: IAirlineRequest): boolean =>
  ['ACCEPTED'].includes((r.submissionStatus || '').toUpperCase()) || r.uiStatus === 'confirmed' || r.uiStatus === 'completed'

const isRejected = (r: IAirlineRequest): boolean =>
  ['REJECTED'].includes((r.submissionStatus || '').toUpperCase()) || r.uiStatus === 'cancelled'

// ===================== StatusPill =====================
const StatusPill: FC<{ label: string; bg: string; fg: string; useClock?: boolean; onDark?: boolean }> = ({
  label,
  bg,
  fg,
  useClock,
  onDark,
}) => {
  const dotColor = label === 'Aprobada' ? accent : fg
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 5,
        paddingHorizontal: 10,
        borderRadius: 999,
        backgroundColor: onDark ? 'rgba(255,255,255,0.12)' : bg,
        alignSelf: 'flex-start',
      }}
    >
      {useClock ? (
        <PendingClock width={12} height={12} />
      ) : (
        <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: dotColor }} />
      )}
      <BodyText style={{ fontSize: 12, fontWeight: '600', letterSpacing: 0.1, paddingLeft: 0, color: onDark ? '#FFFFFF' : fg }}>
        {label}
      </BodyText>
    </View>
  )
}

// ===================== Section eyebrow =====================
const Section: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <View style={{ marginBottom: 24 }}>
    <BodyText
      style={{
        fontSize: 12,
        fontWeight: '600',
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: ink3,
        paddingLeft: 0,
        marginBottom: 12,
      }}
    >
      {title}
    </BodyText>
    {children}
  </View>
)

// ===================== Par dato (label + valor) =====================
const DataPair: FC<{ label: string; value: string }> = ({ label, value }) => (
  <View style={{ minWidth: 130 }}>
    <BodyText style={{ fontSize: 11, fontWeight: '600', letterSpacing: 0.3, color: ink3, paddingLeft: 0, marginBottom: 3 }}>
      {label}
    </BodyText>
    <BodyText style={{ fontSize: 15, fontWeight: '500', letterSpacing: -0.1, color: ink, paddingLeft: 0 }}>{value}</BodyText>
  </View>
)

// ===================== FILTROS =====================
type FilterKey = 'all' | 'pending' | 'approved' | 'rejected'
const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'Todas' },
  { key: 'pending', label: 'Por revisar' },
  { key: 'approved', label: 'Aprobadas' },
  { key: 'rejected', label: 'Rechazadas' },
]

// ===================== BUSCADOR (texto + fecha del vuelo) =====================
// Fila propia ENCIMA de los chips. Texto libre filtra por pasajero/mascota;
// el selector de fecha (input date nativo en web) filtra por día del vuelo.
const SearchBar: FC<{
  query: string
  onQueryChange: (v: string) => void
  dateFilter: string
  onDateChange: (v: string) => void
}> = ({ query, onQueryChange, dateFilter, onDateChange }) => {
  const isWeb = Platform.OS === 'web'
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10,
        marginBottom: 12,
      }}
    >
      {/* Input de texto libre con icono de búsqueda */}
      <View
        style={{
          flexGrow: 1,
          flexBasis: 220,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          backgroundColor: surface,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: hairline,
          paddingHorizontal: 12,
          height: 44,
          ...shadowSoft,
        }}
      >
        <SearchIcon width={16} height={16} style={{ opacity: 0.5 }} />
        <TextInput
          value={query}
          onChangeText={onQueryChange}
          placeholder="Buscar pasajero o mascota"
          placeholderTextColor={ink3}
          accessibilityLabel="Buscar por pasajero o mascota"
          style={
            {
              flex: 1,
              fontSize: 14,
              color: ink,
              paddingVertical: 0,
              ...(isWeb ? { outlineStyle: 'none' as any } : null),
            } as any
          }
        />
        {query.length > 0 && (
          <TouchableOpacity
            onPress={() => onQueryChange('')}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Borrar búsqueda"
            style={{ width: 22, height: 22, borderRadius: 11, backgroundColor: subtle, alignItems: 'center', justifyContent: 'center' }}
          >
            <BodyText style={{ fontSize: 14, lineHeight: 14, color: ink2, paddingLeft: 0 }}>×</BodyText>
          </TouchableOpacity>
        )}
      </View>

      {/* Selector de fecha del vuelo */}
      <View
        style={{
          flexGrow: 0,
          flexBasis: 180,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          backgroundColor: surface,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: dateFilter ? navy : hairline,
          paddingHorizontal: 12,
          height: 44,
          ...shadowSoft,
        }}
      >
        {isWeb ? (
          // Input date nativo: value en yyyy-MM-dd, exactamente la clave de comparación.
          React.createElement('input', {
            type: 'date',
            value: dateFilter,
            'aria-label': 'Filtrar por fecha del vuelo',
            onChange: (e: any) => onDateChange(e.target.value),
            style: {
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 14,
              color: dateFilter ? ink : ink3,
              fontFamily: 'inherit',
              height: '100%',
              width: '100%',
            },
          })
        ) : (
          // Fallback nativo (no-web): texto controlado yyyy-MM-dd.
          <TextInput
            value={dateFilter}
            onChangeText={onDateChange}
            placeholder="aaaa-mm-dd"
            placeholderTextColor={ink3}
            accessibilityLabel="Filtrar por fecha del vuelo"
            style={{ flex: 1, fontSize: 14, color: ink, paddingVertical: 0 }}
          />
        )}
        {dateFilter.length > 0 && (
          <TouchableOpacity
            onPress={() => onDateChange('')}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Quitar filtro de fecha"
            style={{ width: 22, height: 22, borderRadius: 11, backgroundColor: subtle, alignItems: 'center', justifyContent: 'center' }}
          >
            <BodyText style={{ fontSize: 14, lineHeight: 14, color: ink2, paddingLeft: 0 }}>×</BodyText>
          </TouchableOpacity>
        )}
      </View>
    </View>
  )
}

// ===================== COMPONENTE PRINCIPAL =====================
export const AenaAirlinePanel: FC = () => {
  const [requests, setRequests] = useState<IAirlineRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<IAirlineRequest | null>(null)
  const [processing, setProcessing] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterKey>('all')
  const [view, setView] = useState<'cards' | 'table'>('cards')
  const [page, setPage] = useState(0) // base 0
  const [query, setQuery] = useState('') // texto libre: pasajero o mascota
  const [dateFilter, setDateFilter] = useState('') // yyyy-MM-dd del <input type="date">; '' = sin filtro
  const PAGE_SIZE = view === 'cards' ? 9 : 14 // cards 9 (rejilla ~3x3), tabla 14

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await travelApi.getAirlineSubmissions()
      setRequests(response.data || [])
    } catch (e: any) {
      console.error('airline submissions load error', e)
      setError(e?.message || 'No se pudieron cargar las solicitudes')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Bloquea el scroll del body mientras el modal este abierto (solo web).
  useEffect(() => {
    if (Platform.OS !== 'web') return
    const prev = document.body.style.overflow
    document.body.style.overflow = selected ? 'hidden' : prev || ''
    return () => {
      document.body.style.overflow = prev || ''
    }
  }, [selected])

  const openDetail = (req: IAirlineRequest) => {
    setActionError(null)
    setSelected(req)
  }

  const closeDetail = () => {
    if (processing) return
    setSelected(null)
  }

  const handleReview = async (decision: AirlineReviewDecision) => {
    if (!selected) return
    try {
      setProcessing(true)
      setActionError(null)
      await travelApi.reviewTravel(selected.travelId, decision)
      // Al pedir documentos, se notifica al pasajero por email con la lista de los
      // documentos que faltan (plantilla SendGrid, vía el relay del servidor).
      if (decision === 'requires_documents') {
        await travelApi.requestDocumentsEmail(selected.travelId)
      }
      await load()
      setSelected(null)
    } catch (e: any) {
      console.error('review/email error', e)
      setActionError(e?.message || 'No se pudo procesar la decisión')
    } finally {
      setProcessing(false)
    }
  }

  const visibleRequests = useMemo(() => {
    // 1) Chip de estado (lógica intacta).
    let list: IAirlineRequest[]
    switch (filter) {
      case 'pending':
        list = requests.filter(isPending)
        break
      case 'approved':
        list = requests.filter(isApproved)
        break
      case 'rejected':
        list = requests.filter(isRejected)
        break
      default:
        list = requests
    }

    // 2) Texto libre: pasajero (passengerMasked) o mascota (pet.name), case-insensitive.
    const q = query.trim().toLowerCase()
    if (q) {
      list = list.filter((r) => {
        const passenger = (r.passengerMasked || '').toLowerCase()
        const petName = (r.pet?.name || '').toLowerCase()
        return passenger.includes(q) || petName.includes(q)
      })
    }

    // 3) Fecha del vuelo: coincidencia exacta de día (yyyy-MM-dd).
    if (dateFilter) {
      list = list.filter((r) => toISODateKey(r.flightDate) === dateFilter)
    }

    return list
  }, [requests, filter, query, dateFilter])

  // El contador de la cabecera refleja el resultado YA filtrado.
  const pendingCount = useMemo(() => visibleRequests.filter(isPending).length, [visibleRequests])

  // Hay algún filtro activo (texto, fecha o chip distinto de "Todas"): controla el
  // copy y la acción del empty-state.
  const hasActiveSearch = query.trim().length > 0 || dateFilter.length > 0 || filter !== 'all'

  // ===== Paginación en cliente sobre la lista YA filtrada =====
  const totalItems = visibleRequests.length
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1) // clamp defensivo tras filtro/recarga
  const pageItems = useMemo(() => {
    const s = safePage * PAGE_SIZE
    return visibleRequests.slice(s, s + PAGE_SIZE)
  }, [visibleRequests, safePage, PAGE_SIZE])
  const rangeStart = totalItems === 0 ? 0 : safePage * PAGE_SIZE + 1
  const rangeEnd = Math.min(totalItems, (safePage + 1) * PAGE_SIZE)

  // Reset a página 1 al cambiar cualquier filtro (chip/vista/texto/fecha).
  useEffect(() => {
    setPage(0)
  }, [filter, view, query, dateFilter])

  const stickyWeb = Platform.OS === 'web' ? ({ position: 'sticky', top: 0, zIndex: 10 } as any) : undefined

  return (
    <AppBackground isSafe>
      <View style={{ flex: 1, width: '100%', maxWidth: 960, alignSelf: 'center', backgroundColor: canvas }}>
        {/* Cabecera clara */}
        <View
          style={{
            backgroundColor: canvas,
            paddingHorizontal: 20,
            paddingTop: 14,
            paddingBottom: 14,
            borderBottomWidth: 1,
            borderBottomColor: hairline,
            ...shadowSticky,
            ...stickyWeb,
          }}
        >
          {/* Fila 1: branding */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <AenaPetsLogo width={84} height={40} />
            <PetPassLogo width={110} height={38} />
          </View>

          {/* Fila 2: titular + contador */}
          <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 12 }}>
            <View>
              <BodyText
                style={{
                  fontSize: 11,
                  fontWeight: '600',
                  letterSpacing: 1.4,
                  color: ink3,
                  textTransform: 'uppercase',
                  paddingLeft: 0,
                  marginBottom: 2,
                }}
              >
                Panel aerolínea
              </BodyText>
              <Headline1 style={{ fontSize: 26, fontWeight: '300', letterSpacing: -0.4, color: ink, lineHeight: 30 }}>
                Air Europa
              </Headline1>
            </View>
            {pendingCount > 0 ? (
              <StatusPill label={`${pendingCount} por revisar`} bg={warnBg} fg={warnFg} />
            ) : (
              <StatusPill label="Todo al día" bg={okBg} fg={okFg} />
            )}
          </View>
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
          {/* Nota informativa */}
          <View
            style={{
              backgroundColor: infoBg,
              borderRadius: 12,
              padding: 12,
              paddingHorizontal: 16,
              marginBottom: 16,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <AlertTriangle width={16} height={16} />
            <BodyText style={{ fontSize: 12.5, lineHeight: 18, color: ink2, paddingLeft: 0, flex: 1 }}>
              El viaje queda pendiente en "Tus viajes" hasta que lo apruebes aquí.
            </BodyText>
          </View>

          {/* Fila de buscador: texto libre (pasajero/mascota) + fecha del vuelo.
              Va en su propia fila ENCIMA de los chips de filtro. */}
          {!loading && !error && requests.length > 0 && (
            <SearchBar
              query={query}
              onQueryChange={setQuery}
              dateFilter={dateFilter}
              onDateChange={setDateFilter}
            />
          )}

          {/* Fila de control: filtros (izq) + toggle de vista (der) */}
          {!loading && !error && requests.length > 0 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              {/* Chips de filtro (lógica intacta) */}
              <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {FILTERS.map((f) => {
                  const active = filter === f.key
                  return (
                    <TouchableOpacity
                      key={f.key}
                      activeOpacity={0.7}
                      onPress={() => setFilter(f.key)}
                      style={{
                        paddingVertical: 7,
                        paddingHorizontal: 14,
                        borderRadius: 999,
                        backgroundColor: active ? navy : subtle,
                      }}
                    >
                      <BodyText style={{ fontSize: 13, fontWeight: '600', paddingLeft: 0, color: active ? '#FFFFFF' : ink2 }}>
                        {f.label}
                      </BodyText>
                    </TouchableOpacity>
                  )
                })}
              </View>

              {/* Toggle de vista (segmented control inset, glifos inline) */}
              <View
                style={{
                  flexDirection: 'row',
                  backgroundColor: subtle,
                  borderRadius: 12,
                  padding: 3,
                  borderWidth: 1,
                  borderColor: hairline,
                  marginLeft: 12,
                  flexShrink: 0,
                }}
              >
                <ViewToggleCell mode="cards" view={view} onPress={() => setView('cards')} />
                <ViewToggleCell mode="table" view={view} onPress={() => setView('table')} />
              </View>
            </View>
          )}

          {loading ? (
            <View style={{ alignItems: 'center', paddingVertical: 60 }}>
              <ActivityIndicator size="large" color={accent} />
              <BodyText style={{ marginTop: 12, color: ink2, paddingLeft: 0 }}>Cargando solicitudes…</BodyText>
            </View>
          ) : error ? (
            <View style={{ alignItems: 'center', paddingVertical: 60 }}>
              <AlertTriangle width={32} height={32} />
              <BodyText style={{ color: blockFg, marginVertical: 16, paddingLeft: 0, textAlign: 'center' }}>{error}</BodyText>
              <TouchableOpacity
                onPress={load}
                activeOpacity={0.7}
                style={{
                  borderWidth: 1,
                  borderColor: hairline,
                  backgroundColor: surface,
                  paddingVertical: 12,
                  paddingHorizontal: 24,
                  borderRadius: 12,
                }}
              >
                <BodyText style={{ fontWeight: '600', color: ink, paddingLeft: 0 }}>Reintentar</BodyText>
              </TouchableOpacity>
            </View>
          ) : visibleRequests.length === 0 ? (
            <View style={{ alignItems: 'center', paddingVertical: 60 }}>
              <PawColor width={40} height={40} style={{ opacity: 0.6 }} />
              <BodyText style={{ marginTop: 14, color: ink2, paddingLeft: 0, textAlign: 'center' }}>
                {hasActiveSearch
                  ? 'No hay solicitudes que coincidan con la búsqueda.'
                  : 'No hay solicitudes pendientes.'}
              </BodyText>
              {hasActiveSearch && (
                <TouchableOpacity
                  onPress={() => {
                    setQuery('')
                    setDateFilter('')
                    setFilter('all')
                  }}
                  activeOpacity={0.7}
                  style={{
                    marginTop: 16,
                    borderWidth: 1,
                    borderColor: hairline,
                    backgroundColor: surface,
                    paddingVertical: 10,
                    paddingHorizontal: 20,
                    borderRadius: 12,
                  }}
                >
                  <BodyText style={{ fontSize: 13, fontWeight: '600', color: ink, paddingLeft: 0 }}>Limpiar búsqueda</BodyText>
                </TouchableOpacity>
              )}
            </View>
          ) : view === 'cards' ? (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 14, rowGap: 14 }}>
              {pageItems.map((req) => (
                <View key={req.requestId} style={{ flexGrow: 1, flexBasis: 300, maxWidth: '100%' }}>
                  <RequestCard req={req} onPress={() => openDetail(req)} />
                </View>
              ))}
            </View>
          ) : (
            <RequestTable items={pageItems} onRowPress={openDetail} />
          )}

          {/* Paginación (solo si hay más de una página) */}
          {!loading && !error && visibleRequests.length > 0 && totalPages > 1 && (
            <Pagination
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              totalItems={totalItems}
              safePage={safePage}
              totalPages={totalPages}
              onPrev={() => setPage((p) => Math.max(0, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            />
          )}
        </ScrollView>
      </View>

      <DetailModal
        req={selected}
        processing={processing}
        actionError={actionError}
        onClose={closeDetail}
        onReview={handleReview}
      />
    </AppBackground>
  )
}

// ============================================================================
// LOGIN HARDCODEADO (solo demo de vídeo)
// Puerta de acceso al panel de la aerolínea. NO es autenticación real: las
// credenciales están fijadas en el cliente y solo sirven para la demostración.
// La sesión se recuerda en sessionStorage (se reinicia al cerrar la pestaña).
// ============================================================================
const DEMO_AIRLINE_USER = 'aireuropa'
const DEMO_AIRLINE_PASS = 'aena2026'
const AIRLINE_SESSION_KEY = 'aena_airline_demo_authed'

const AenaAirlineLogin: FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const [user, setUser] = useState('')
  const [pass, setPass] = useState('')
  const [error, setError] = useState('')

  const submit = () => {
    if (user.trim().toLowerCase() === DEMO_AIRLINE_USER && pass === DEMO_AIRLINE_PASS) {
      try { window.sessionStorage.setItem(AIRLINE_SESSION_KEY, '1') } catch { /* noop */ }
      setError('')
      onSuccess()
    } else {
      setError('Usuario o contraseña incorrectos.')
    }
  }

  const inputStyle = {
    borderWidth: 1,
    borderColor: hairline,
    borderRadius: 12,
    paddingHorizontal: 18,
    paddingVertical: 18,
    fontSize: 18,
    color: ink,
    backgroundColor: subtle,
  } as const

  return (
    <AppBackground isSafe>
      <ScrollView contentContainerStyle={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center', padding: 28, backgroundColor: canvas }}>
        <View
          style={{
            width: '100%',
            maxWidth: 600,
            backgroundColor: surface,
            borderRadius: 24,
            borderWidth: 1,
            borderColor: hairline,
            paddingVertical: 52,
            paddingHorizontal: 48,
            ...shadowCard,
          }}
        >
          {/* Logos alineados (mismo ResponsiveLogo que el resto de páginas) */}
          <View style={{ alignItems: 'center', marginBottom: 20 }}>
            <ResponsiveLogo
              breakpoint={820}
              hysteresis={20}
              large={{ width: 480, height: 70 }}
              small={{ width: 380, height: 56 }}
            />
          </View>

          <Headline1 style={{ textAlign: 'center', fontSize: 32, fontWeight: '700', color: ink, marginTop: 16 }}>
            Check-in Aerolínea
          </Headline1>
          <BodyText style={{ textAlign: 'center', color: ink2, paddingLeft: 0, marginTop: 8, marginBottom: 40, fontSize: 17 }}>
            Air Europa · Acceso operador
          </BodyText>

          <BodyText style={{ color: ink2, paddingLeft: 0, marginBottom: 10, fontSize: 15, fontWeight: '600' }}>Usuario</BodyText>
          <TextInput
            value={user}
            onChangeText={(v) => { setUser(v); if (error) setError('') }}
            placeholder="Usuario"
            placeholderTextColor={ink3}
            autoCapitalize="none"
            autoCorrect={false}
            style={inputStyle}
          />

          <BodyText style={{ color: ink2, paddingLeft: 0, marginBottom: 10, marginTop: 26, fontSize: 15, fontWeight: '600' }}>Contraseña</BodyText>
          <TextInput
            value={pass}
            onChangeText={(v) => { setPass(v); if (error) setError('') }}
            placeholder="Contraseña"
            placeholderTextColor={ink3}
            secureTextEntry
            autoCapitalize="none"
            autoCorrect={false}
            onSubmitEditing={submit}
            style={inputStyle}
          />

          {!!error && (
            <BodyText style={{ color: blockFg, paddingLeft: 0, marginTop: 16, fontSize: 15 }}>{error}</BodyText>
          )}

          <TouchableOpacity
            onPress={submit}
            activeOpacity={0.85}
            style={{
              marginTop: 36,
              backgroundColor: accent,
              borderRadius: 14,
              paddingVertical: 20,
              alignItems: 'center',
              ...shadowAccept,
            }}
          >
            <BodyText style={{ color: accentInk, paddingLeft: 0, fontSize: 20, fontWeight: '700' }}>Entrar</BodyText>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </AppBackground>
  )
}

// Gate: muestra el login si no hay sesión de demo; si la hay, el panel.
const AenaAirlineGate: FC = () => {
  const [authed, setAuthed] = useState<boolean>(() => {
    try { return window.sessionStorage.getItem(AIRLINE_SESSION_KEY) === '1' } catch { return false }
  })
  if (!authed) return <AenaAirlineLogin onSuccess={() => setAuthed(true)} />
  return <AenaAirlinePanel />
}

export default AenaAirlineGate

// ===================== CARD DE LISTA =====================
const RequestCard: FC<{ req: IAirlineRequest; onPress: () => void }> = ({ req, onPress }) => {
  const meta = statusMeta(req)
  const { Icon: SpeciesIcon, tint } = speciesAvatar(req.pet?.species)
  const { valid, total, allOk } = validationCount(req)

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.9}
      style={{
        backgroundColor: surface,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: hairline,
        padding: 18,
        ...shadowCard,
      }}
    >
      {/* Fila identidad */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        <View
          style={{ width: 44, height: 44, borderRadius: 14, backgroundColor: tint, alignItems: 'center', justifyContent: 'center' }}
        >
          <SpeciesIcon width={24} height={24} />
        </View>
        <View style={{ flex: 1 }}>
          <BodyText style={{ fontSize: 19, fontWeight: '600', letterSpacing: -0.2, color: ink, paddingLeft: 0 }}>
            {flightLabel(req)}
          </BodyText>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3 }}>
            <BodyText style={{ fontSize: 13.5, fontWeight: '500', color: ink2, paddingLeft: 0 }}>
              {req.departureAirport || 'MAD'}
            </BodyText>
            <AirportIcon width={12} height={12} style={{ opacity: 0.55 }} />
            <BodyText style={{ fontSize: 13.5, fontWeight: '500', color: ink2, paddingLeft: 0 }}>
              {req.arrivalAirport || 'BCN'}
            </BodyText>
            {!!req.pet?.name && (
              <BodyText style={{ fontSize: 12, color: ink3, paddingLeft: 0, marginLeft: 2 }}>{`· ${req.pet.name}`}</BodyText>
            )}
          </View>
        </View>
        <StatusPill label={meta.label} bg={meta.bg} fg={meta.fg} useClock={meta.useClock} />
      </View>

      {/* Separador */}
      <View style={{ height: 1, backgroundColor: hairline, marginVertical: 14 }} />

      {/* Zona datos */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', rowGap: 12, columnGap: 24 }}>
        <DataPair label="Fecha" value={formatFlightDate(req.flightDate)} />
        <DataPair label="Pasajero" value={req.passengerMasked || '—'} />
        <DataPair label="Localizador" value={req.locatorMasked || '—'} />
        <DataPair label="Transporte" value={transportLabel(req.pet)} />
      </View>

      {/* Pie: medidor + affordance */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
        {total > 0 ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <BodyText style={{ fontSize: 13, fontWeight: '700', paddingLeft: 0, color: allOk ? okFg : warnFg }}>
              {`${valid}/${total}`}
            </BodyText>
            <View style={{ flexDirection: 'row', gap: 3 }}>
              {visibleValidations(req).map((v, i) => {
                const s = (v.status || '').toUpperCase()
                const c = s === 'VALID' ? okFg : s === 'MISSING' || s === 'INVALID' || s === 'REJECTED' ? blockFg : warnFg
                return <View key={`${v.type}-${i}`} style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: c }} />
              })}
            </View>
            <BodyText
              style={{ fontSize: 9.5, fontWeight: '600', letterSpacing: 0.5, color: ink3, textTransform: 'uppercase', paddingLeft: 0 }}
            >
              Validaciones
            </BodyText>
          </View>
        ) : (
          <View />
        )}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <BodyText style={{ fontSize: 12.5, fontWeight: '600', color: ink2, paddingLeft: 0 }}>Revisar</BodyText>
          <ChevronRight width={16} height={16} style={{ opacity: 0.5 }} />
        </View>
      </View>
    </TouchableOpacity>
  )
}

// ===================== TOGGLE DE VISTA (glifos inline, sin SVG nuevo) =====================
const ViewToggleCell: FC<{ mode: 'cards' | 'table'; view: 'cards' | 'table'; onPress: () => void }> = ({ mode, view, onPress }) => {
  const active = view === mode
  return (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={mode === 'cards' ? 'Vista de tarjetas' : 'Vista de tabla'}
      accessibilityState={{ selected: active }}
      style={{
        width: 34,
        height: 28,
        borderRadius: 9,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: active ? surface : 'transparent',
        ...(active ? shadowSoft : null),
      }}
    >
      {mode === 'cards' ? (
        // GRID: 2x2 cuadraditos
        <View
          style={{
            width: 16,
            height: 16,
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignContent: 'space-between',
          }}
        >
          {[0, 1, 2, 3].map((k) => (
            <View key={k} style={{ width: 6.5, height: 6.5, borderRadius: 2, backgroundColor: view === 'cards' ? navy : ink3 }} />
          ))}
        </View>
      ) : (
        // ROWS: 3 barras horizontales (la 3ª más corta = lectura inmediata de lista)
        <View style={{ width: 16, height: 16, justifyContent: 'space-between' }}>
          {[0, 1, 2].map((k) => (
            <View key={k} style={{ height: 3, borderRadius: 1.5, width: k === 2 ? 11 : 16, backgroundColor: view === 'table' ? navy : ink3 }} />
          ))}
        </View>
      )}
    </TouchableOpacity>
  )
}

// ===================== VISTA TABLA (filas condensadas de 2 líneas) =====================
const RequestTable: FC<{ items: IAirlineRequest[]; onRowPress: (req: IAirlineRequest) => void }> = ({ items, onRowPress }) => (
  <View
    style={{
      backgroundColor: surface,
      borderRadius: 18,
      borderWidth: 1,
      borderColor: hairline,
      overflow: 'hidden',
      ...shadowCard,
    }}
  >
    {/* Header sutil */}
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingTop: 13,
        paddingBottom: 9,
        backgroundColor: subtle,
        borderBottomWidth: 1,
        borderBottomColor: hairline,
      }}
    >
      <BodyText style={{ fontSize: 10.5, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase', color: ink3, paddingLeft: 0 }}>
        Solicitud
      </BodyText>
      <BodyText style={{ fontSize: 10.5, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase', color: ink3, paddingLeft: 0 }}>
        Estado
      </BodyText>
    </View>

    {/* Filas */}
    {items.map((req, i) => {
      const meta = statusMeta(req)
      const { Icon: SpeciesIcon, tint } = speciesAvatar(req.pet?.species)
      const { valid, total, allOk } = validationCount(req)
      const speciesName = (req.pet?.species || '').toUpperCase() === 'CAT' ? 'Gato' : 'Perro'
      const petPax = `${req.pet?.name ? `${req.pet.name} (${speciesName})` : '—'} · ${req.passengerMasked || '—'}`
      return (
        <TouchableOpacity
          key={req.requestId}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={`Revisar ${flightLabel(req)} ${routeText(req)}`}
          onPress={() => onRowPress(req)}
          style={{
            paddingVertical: 12,
            paddingHorizontal: 16,
            backgroundColor: i % 2 === 1 ? '#FCFCFD' : surface,
            ...(i > 0 ? { borderTopWidth: 1, borderTopColor: hairline } : null),
          }}
        >
          {/* Línea 1: identidad + estado */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <View style={{ width: 30, height: 30, borderRadius: 9, backgroundColor: tint, alignItems: 'center', justifyContent: 'center' }}>
              <SpeciesIcon width={17} height={17} />
            </View>
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
              <BodyText style={{ fontSize: 15.5, fontWeight: '600', letterSpacing: -0.2, color: ink, paddingLeft: 0 }}>
                {flightLabel(req)}
              </BodyText>
              <BodyText numberOfLines={1} style={{ flex: 1, fontSize: 12.5, fontWeight: '500', color: ink2, paddingLeft: 0 }}>
                {routeText(req)}
              </BodyText>
            </View>
            <View style={{ flexShrink: 0 }}>
              <StatusPill label={meta.label} bg={meta.bg} fg={meta.fg} useClock={meta.useClock} />
            </View>
          </View>

          {/* Línea 2: mascota+pasajero + x/total + chevron (alineada bajo el texto) */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 7, paddingLeft: 40 }}>
            <BodyText numberOfLines={1} style={{ flex: 1, fontSize: 12.5, color: ink2, paddingLeft: 0 }}>
              {petPax}
            </BodyText>
            <BodyText
              style={{ fontSize: 12, fontWeight: '700', color: total === 0 ? ink3 : allOk ? okFg : warnFg, paddingLeft: 0, flexShrink: 0 }}
            >
              {total === 0 ? '—' : `${valid}/${total}`}
            </BodyText>
            <ChevronRight width={16} height={16} style={{ opacity: 0.45 }} />
          </View>
        </TouchableOpacity>
      )
    })}
  </View>
)

// ===================== PAGINACIÓN (footer discreto, rango + prev/next) =====================
const Pagination: FC<{
  rangeStart: number
  rangeEnd: number
  totalItems: number
  safePage: number
  totalPages: number
  onPrev: () => void
  onNext: () => void
}> = ({ rangeStart, rangeEnd, totalItems, safePage, totalPages, onPrev, onNext }) => {
  const prevDisabled = safePage === 0
  const nextDisabled = safePage >= totalPages - 1
  const navBtn = (disabled: boolean) => ({
    width: 36,
    height: 36,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: hairline,
    backgroundColor: surface,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    ...shadowSoft,
    opacity: disabled ? 0.35 : 1,
  })
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        rowGap: 10,
        marginTop: 18,
        paddingVertical: 6,
      }}
    >
      <BodyText style={{ fontSize: 12.5, fontWeight: '600', color: ink2, paddingLeft: 0 }}>
        {`${rangeStart}–${rangeEnd} de ${totalItems}`}
      </BodyText>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <TouchableOpacity
          activeOpacity={0.7}
          disabled={prevDisabled}
          accessibilityRole="button"
          accessibilityLabel="Página anterior"
          onPress={onPrev}
          style={navBtn(prevDisabled)}
        >
          <ChevronRight width={16} height={16} style={{ opacity: 0.65, transform: 'scaleX(-1)' }} />
        </TouchableOpacity>
        <BodyText style={{ fontSize: 12.5, fontWeight: '600', color: ink2, paddingLeft: 0, marginHorizontal: 2 }}>
          {`${safePage + 1} / ${totalPages}`}
        </BodyText>
        <TouchableOpacity
          activeOpacity={0.7}
          disabled={nextDisabled}
          accessibilityRole="button"
          accessibilityLabel="Página siguiente"
          onPress={onNext}
          style={navBtn(nextDisabled)}
        >
          <ChevronRight width={16} height={16} style={{ opacity: 0.65 }} />
        </TouchableOpacity>
      </View>
    </View>
  )
}

// ===================== MODAL DE DETALLE =====================
const DetailModal: FC<{
  req: IAirlineRequest | null
  processing: boolean
  actionError: string | null
  onClose: () => void
  onReview: (decision: AirlineReviewDecision) => void
}> = ({ req, processing, actionError, onClose, onReview }) => {
  if (!req) return null
  const meta = statusMeta(req)
  const { Icon: SpeciesIcon, tint } = speciesAvatar(req.pet?.species)
  const { valid, total } = validationCount(req)
  const subtitle = `${routeText(req)} · ${formatFlightDate(req.flightDate)}`

  const stickyTop = Platform.OS === 'web' ? ({ position: 'sticky', top: 0, zIndex: 10 } as any) : undefined
  const stickyBottom = Platform.OS === 'web' ? ({ position: 'sticky', bottom: 0, zIndex: 10 } as any) : undefined

  const speciesName = req.pet?.species ? ((req.pet.species || '').toUpperCase() === 'CAT' ? 'Gato' : 'Perro') : ''
  const petValue = req.pet?.name ? `${req.pet.name}${speciesName ? ` · ${speciesName}` : ''}` : '—'

  return (
    <Modal visible animationType="slide" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: canvas, maxWidth: 960, width: '100%', alignSelf: 'center' }}>
        {/* Cabecera modal */}
        <View
          style={{
            backgroundColor: surface,
            paddingHorizontal: 20,
            paddingTop: 18,
            paddingBottom: 16,
            borderBottomWidth: 1,
            borderBottomColor: hairline,
            ...shadowSticky,
            ...stickyTop,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <View style={{ flexDirection: 'row', gap: 12, flex: 1 }}>
              <View
                style={{ width: 40, height: 40, borderRadius: 13, backgroundColor: tint, alignItems: 'center', justifyContent: 'center' }}
              >
                <SpeciesIcon width={22} height={22} />
              </View>
              <View style={{ flex: 1 }}>
                <BodyText style={{ fontSize: 22, fontWeight: '600', letterSpacing: -0.3, color: ink, paddingLeft: 0 }}>
                  {flightLabel(req)}
                </BodyText>
                <BodyText style={{ fontSize: 13, color: ink2, paddingLeft: 0, marginTop: 2 }}>{subtitle}</BodyText>
              </View>
            </View>
            <TouchableOpacity
              onPress={onClose}
              disabled={processing}
              activeOpacity={0.7}
              style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: subtle, alignItems: 'center', justifyContent: 'center' }}
            >
              <BodyText style={{ fontSize: 18, lineHeight: 18, color: ink2, paddingLeft: 0 }}>×</BodyText>
            </TouchableOpacity>
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
            <StatusPill label={meta.label} bg={meta.bg} fg={meta.fg} useClock={meta.useClock} />
            {total > 0 && (
              <BodyText style={{ fontSize: 12.5, color: ink2, paddingLeft: 0 }}>{`${valid}/${total} validaciones`}</BodyText>
            )}
          </View>
        </View>

        {/* Cuerpo */}
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20, paddingBottom: 140 }}>
          {/* Sección 1: datos */}
          <Section title="Datos de la solicitud">
            <View style={{ backgroundColor: surface, borderRadius: 16, borderWidth: 1, borderColor: hairline, padding: 16, ...shadowSoft }}>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', rowGap: 12, columnGap: 24 }}>
                <DataPair label="Pasajero" value={req.passengerMasked || '—'} />
                <DataPair label="Localizador" value={req.locatorMasked || '—'} />
                <DataPair label="Mascota" value={petValue} />
                <DataPair label="Microchip" value={req.pet?.microchipMasked || '—'} />
                <DataPair label="Transporte" value={transportLabel(req.pet)} />
                {!!req.airlineReference && <DataPair label="Referencia" value={req.airlineReference} />}
              </View>
            </View>
          </Section>

          {/* Sección 2: checklist */}
          <Section title="Checklist de validaciones">
            <View style={{ backgroundColor: surface, borderRadius: 16, borderWidth: 1, borderColor: hairline, padding: 16, ...shadowSoft }}>
              {visibleValidations(req).length === 0 ? (
                <BodyText style={{ fontSize: 14, color: ink2, paddingLeft: 0 }}>Sin validaciones registradas.</BodyText>
              ) : (
                visibleValidations(req).map((v, i) => {
                  const vMeta = validationMeta(v.status)
                  const VIcon = vMeta.Icon
                  return (
                    <View
                      key={`${v.type}-${i}`}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 12,
                        paddingVertical: 12,
                        ...(i > 0 ? { borderTopWidth: 1, borderTopColor: hairline } : {}),
                      }}
                    >
                      <View
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 12,
                          backgroundColor: vMeta.bg,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <VIcon width={14} height={14} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <BodyText style={{ fontSize: 14.5, fontWeight: '600', color: ink, paddingLeft: 0 }}>
                          {validationLabel(v.type)}
                        </BodyText>
                        {!!v.message && (
                          <BodyText style={{ fontSize: 12.5, lineHeight: 18, color: ink2, paddingLeft: 0, marginTop: 2 }}>
                            {v.message}
                          </BodyText>
                        )}
                      </View>
                      <View style={{ paddingVertical: 3, paddingHorizontal: 8, borderRadius: 999, backgroundColor: vMeta.bg }}>
                        <BodyText style={{ fontSize: 11, fontWeight: '600', color: vMeta.fg, paddingLeft: 0 }}>{vMeta.label}</BodyText>
                      </View>
                    </View>
                  )
                })
              )}
            </View>
          </Section>

          {/* Sección 3: documentos */}
          <Section title="Documentos">
            <View style={{ backgroundColor: surface, borderRadius: 16, borderWidth: 1, borderColor: hairline, padding: 16, ...shadowSoft }}>
              {visibleDocuments(req).length === 0 ? (
                <BodyText style={{ fontSize: 14, color: ink2, paddingLeft: 0 }}>Sin documentos adjuntos.</BodyText>
              ) : (
                visibleDocuments(req).map((doc, i) => {
                  const cfg = docConfig(doc.type)
                  const DIcon = cfg.Icon
                  const dMeta = doc.validationStatus ? validationMeta(doc.validationStatus) : null
                  return (
                    <View
                      key={`${doc.type}-${i}`}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 12,
                        paddingVertical: 12,
                        ...(i > 0 ? { borderTopWidth: 1, borderTopColor: hairline } : {}),
                      }}
                    >
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 12,
                          backgroundColor: subtle,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <DIcon width={20} height={20} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <BodyText style={{ fontSize: 14.5, fontWeight: '600', color: ink, paddingLeft: 0 }}>{cfg.label}</BodyText>
                        {!!dMeta && (
                          <View
                            style={{
                              alignSelf: 'flex-start',
                              paddingVertical: 3,
                              paddingHorizontal: 8,
                              borderRadius: 999,
                              backgroundColor: dMeta.bg,
                              marginTop: 4,
                            }}
                          >
                            <BodyText style={{ fontSize: 11, fontWeight: '600', color: dMeta.fg, paddingLeft: 0 }}>
                              {dMeta.label}
                            </BodyText>
                          </View>
                        )}
                      </View>
                      <TouchableOpacity
                        onPress={() => openStoredDocument(doc.url)}
                        disabled={!doc.url}
                        activeOpacity={0.7}
                        style={{
                          borderWidth: 1,
                          borderColor: hairline,
                          backgroundColor: surface,
                          paddingVertical: 9,
                          paddingHorizontal: 16,
                          borderRadius: 10,
                          opacity: doc.url ? 1 : 0.4,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <BodyText style={{ fontSize: 13, fontWeight: '600', color: ink, paddingLeft: 0 }}>Abrir</BodyText>
                        <ChevronRight width={14} height={14} style={{ opacity: 0.5 }} />
                      </TouchableOpacity>
                    </View>
                  )
                })
              )}

              <TouchableOpacity
                onPress={() => downloadJson(`solicitud-${req.requestId}.json`, req.requestPayload)}
                activeOpacity={0.7}
                style={{ marginTop: 12, borderWidth: 1, borderColor: hairline, paddingVertical: 12, borderRadius: 12, alignItems: 'center' }}
              >
                <BodyText style={{ fontSize: 13, fontWeight: '600', color: ink2, paddingLeft: 0 }}>
                  Ver/Descargar JSON enviado
                </BodyText>
              </TouchableOpacity>
            </View>
          </Section>

          {/* Sección 4: documentación de asistencia (SOLO animales de asistencia:
              invidente / sordo / otra). Se marcan como aportados. Si no es de
              asistencia, no se muestra. */}
          {isAssistancePet(req.pet) && (
            <Section title="Documentación de asistencia">
              <View style={{ backgroundColor: surface, borderRadius: 16, borderWidth: 1, borderColor: hairline, padding: 16, ...shadowSoft }}>
                {ASSISTANCE_REQUIRED_DOCS.map((label, i) => {
                  const okMeta = validationMeta('VALID')
                  const OkIcon = okMeta.Icon
                  return (
                    <View
                      key={`assist-${i}`}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 12,
                        paddingVertical: 12,
                        ...(i > 0 ? { borderTopWidth: 1, borderTopColor: hairline } : {}),
                      }}
                    >
                      <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: okMeta.bg, alignItems: 'center', justifyContent: 'center' }}>
                        <OkIcon width={14} height={14} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <BodyText style={{ fontSize: 14.5, fontWeight: '600', color: ink, paddingLeft: 0 }}>{label}</BodyText>
                      </View>
                      <View style={{ paddingVertical: 3, paddingHorizontal: 8, borderRadius: 999, backgroundColor: okMeta.bg }}>
                        <BodyText style={{ fontSize: 11, fontWeight: '600', color: okMeta.fg, paddingLeft: 0 }}>Aportado</BodyText>
                      </View>
                    </View>
                  )
                })}
              </View>
            </Section>
          )}

          {/* Nota final */}
          <View style={{ backgroundColor: infoBg, borderRadius: 12, padding: 12, paddingHorizontal: 14 }}>
            <BodyText style={{ fontSize: 12.5, color: ink2, lineHeight: 18, paddingLeft: 0 }}>
              Al aprobar, el viaje pasa a validado en "Tus viajes".
            </BodyText>
          </View>
        </ScrollView>

        {/* Footer de acciones fijo */}
        <View
          style={{
            backgroundColor: surface,
            borderTopWidth: 1,
            borderTopColor: hairline,
            paddingHorizontal: 16,
            paddingTop: 14,
            paddingBottom: 20,
            ...shadowFooter,
            ...stickyBottom,
          }}
        >
          {actionError && (
            <View
              style={{
                backgroundColor: blockBg,
                borderRadius: 12,
                padding: 12,
                marginBottom: 12,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <AlertTriangle width={14} height={14} />
              <BodyText style={{ fontSize: 13, color: blockFg, paddingLeft: 0, flex: 1 }}>{actionError}</BodyText>
            </View>
          )}

          {processing ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, paddingVertical: 14 }}>
              <ActivityIndicator color={accent} />
              <BodyText style={{ fontSize: 14, fontWeight: '600', color: ink2, paddingLeft: 0 }}>Procesando…</BodyText>
            </View>
          ) : (
            <View>
              <TouchableOpacity
                onPress={() => onReview('accepted')}
                activeOpacity={0.85}
                style={{
                  backgroundColor: accent,
                  paddingVertical: 15,
                  borderRadius: 14,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  ...shadowAccept,
                }}
              >
                <RoundedCheck width={18} height={18} />
                <BodyText style={{ fontSize: 15.5, fontWeight: '700', color: accentInk, paddingLeft: 0 }}>Aprobar</BodyText>
              </TouchableOpacity>

              <View style={{ flexDirection: 'row', gap: 10, marginTop: 10 }}>
                <TouchableOpacity
                  onPress={() => onReview('requires_documents')}
                  activeOpacity={0.85}
                  style={{ flex: 1, backgroundColor: warnBg, paddingVertical: 13, borderRadius: 12, alignItems: 'center' }}
                >
                  <BodyText style={{ fontSize: 14, fontWeight: '600', color: warnFg, paddingLeft: 0 }}>Pedir documentos</BodyText>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => onReview('rejected')}
                  activeOpacity={0.85}
                  style={{
                    flex: 1,
                    backgroundColor: surface,
                    borderWidth: 1,
                    borderColor: '#F0D9D9',
                    paddingVertical: 13,
                    borderRadius: 12,
                    alignItems: 'center',
                  }}
                >
                  <BodyText style={{ fontSize: 14, fontWeight: '600', color: blockFg, paddingLeft: 0 }}>Rechazar</BodyText>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  )
}
